import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Label for Sentry, e.g. "cases" */
  routeName?: string;
};

type State = {
  error: Error | null;
};

/**
 * Catches render errors in a single route so the shell (nav, tabs) stays visible.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { route: this.props.routeName ?? 'unknown' },
    });
  }

  private handleRetry = () => {
    const msg = this.state.error?.message ?? '';
    if (msg.includes('INTERNAL ASSERTION FAILED')) {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const isFirestoreInternal = this.state.error.message.includes('INTERNAL ASSERTION FAILED');
      return (
        <div className="app-page flex min-h-[40vh] flex-col items-center justify-center px-6 py-16 text-center">
          <h2 className="text-lg font-semibold text-text-main">This page failed to load</h2>
          <p className="mt-2 max-w-md text-sm text-[#64748B]">
            {isFirestoreInternal
              ? 'The database connection hit a temporary glitch. Reload the page to restore it.'
              : this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="btn-primary mt-6"
          >
            {isFirestoreInternal ? 'Reload page' : 'Try again'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
